import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "ui";

import { AiLesson, AiLessonGuidanceIssue } from "../../aiAgents.types";
import { useLessonMutations } from "../../apis";
import {
  LessonFormValue,
  lessonEditResetsToDraft,
  lessonToFormValue,
  parseLessonApiError,
  toUpdateLessonBody,
  validateLessonForm
} from "../../helpers/lessons";
import { LessonForm } from "../LessonForm";

import styles from "./LessonEditModal.module.scss";

interface Props {
  assistantId: string;
  // Open when set.
  lesson: AiLesson | null;
  onClose: () => void;
  // The lessons backend answered 404 (not deployed): the caller hides the tab.
  onUnavailable?: () => void;
}

const EditForm = ({
  assistantId,
  lesson,
  onClose,
  onUnavailable
}: Props & { lesson: AiLesson }) => {
  const { t } = useTranslation();
  const { updateLesson } = useLessonMutations(assistantId);
  const [form, setForm] = useState<LessonFormValue>(() =>
    lessonToFormValue(lesson)
  );
  const [issues, setIssues] = useState<AiLessonGuidanceIssue[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const resetsToDraft = lessonEditResetsToDraft(lesson, form);

  const handleSave = () => {
    if (updateLesson.isLoading) return;
    const formErrors = validateLessonForm(form);
    setIssues([]);
    setErrors(formErrors);
    if (formErrors.length > 0) return;

    // Only what changed is sent, so a title fix never bumps the version.
    const body = toUpdateLessonBody(lesson, form);
    if (!body) {
      onClose();
      return;
    }

    updateLesson.mutate(body, {
      onSuccess: () => {
        toast.success(t("aiLesson.toast.updated"));
        onClose();
      },
      onError: (error) => {
        const details = parseLessonApiError(error);
        if (details.endpointMissing) {
          onUnavailable?.();
          onClose();
          return;
        }
        setIssues(details.issues);
        setErrors(
          details.messages.length > 0
            ? details.messages
            : ["aiLesson.edit.saveFailed"]
        );
      }
    });
  };

  return (
    <Modal
      isOpen
      title="aiLesson.edit.title"
      onClose={onClose}
      footer={
        <div className={styles.footer}>
          {errors.length > 0 ? (
            <div className={styles.errorBox}>
              {errors.map((message) => (
                <div key={message}>{t(message)}</div>
              ))}
            </div>
          ) : null}
          <div className={styles.footerButtons}>
            <Button type="button" variant="default" block onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              block
              loading={updateLesson.isLoading}
              onClick={handleSave}
            >
              {t("aiLesson.edit.save")}
            </Button>
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {resetsToDraft ? (
          <div className={styles.warning}>
            {t("aiLesson.edit.backToDraftWarning")}
          </div>
        ) : null}
        <LessonForm
          mode="edit"
          value={form}
          onChange={setForm}
          issues={issues}
          disabled={updateLesson.isLoading}
        />
      </div>
    </Modal>
  );
};

// Lessons tab: edit the title, type, situation, guidance and triggers of a
// lesson (X, Y and the A/B versions stay read-only). Changing the guidance or
// the triggers puts a non-draft lesson back to draft, which the modal says.
const LessonEditModal = ({ lesson, ...props }: Props) =>
  // keyed by lesson and version, so reopening starts from the saved values
  lesson ? (
    <EditForm
      key={`${lesson.id}:${lesson.version}`}
      lesson={lesson}
      {...props}
    />
  ) : null;

export default LessonEditModal;
